// The chat app shell — what an org's users see, and where admins and engineers
// go to use (rather than build) agents.
//
// Deliberately not the console: no build navigation, just threads on the left
// and the conversation on the right. It is its own session gate, the same
// checks AppLayout makes (an expired session logs out, a pending temp password
// goes to change-password); a superadmin has no org to chat in and is sent to
// the console.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  LayoutGrid,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  Pencil,
  Search,
  Settings2,
  SquarePen,
  Sun,
  Trash2,
  X,
} from "lucide-react";
import * as React from "react";
import { Outlet } from "react-router-dom";

import { Brand } from "@/components/brand";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Link } from "@/components/ui/link";
import { Button, Skeleton } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import {
  deleteConversation,
  listConversations,
  listOrgConversations,
  renameConversation,
} from "@/lib/api/chat";
import { logout } from "@/lib/api/endpoints";
import { forceLogout } from "@/lib/api/session";
import { usePathname, useRouter } from "@/lib/navigation";
import { useMe } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import type { ChatConversation } from "@/types/api";

export default function ChatLayout() {
  const router = useRouter();
  const { data: me, isLoading, isError } = useMe();
  const [navOpen, setNavOpen] = React.useState(false);
  const pathname = usePathname();

  React.useEffect(() => {
    if (isError) void forceLogout("expired");
    else if (me?.must_change_password) router.replace("/change-password");
    else if (me?.role === "superadmin") router.replace("/dashboard");
  }, [isError, me, router]);

  // Close the mobile drawer on navigation.
  React.useEffect(() => setNavOpen(false), [pathname]);

  if (isLoading || isError || !me || me.must_change_password || me.role === "superadmin") {
    return (
      <div className="flex h-screen">
        <div className="hidden w-72 border-r border-border bg-card p-4 md:block">
          <Skeleton className="mb-6 h-9 w-32" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="mx-auto h-64 w-full max-w-3xl" />
        </div>
      </div>
    );
  }

  const builder = me.role === "tenant_admin" || me.role === "engineer";

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile scrim */}
      {navOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      ) : null}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-border bg-card transition-transform md:static md:translate-x-0",
          navOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <Brand />
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-1 px-3">
          <Button asChild variant="outline" className="w-full justify-start">
            <Link href="/chat">
              <SquarePen className="h-4 w-4" />
              New chat
            </Link>
          </Button>
          <Link
            href="/chat/agents"
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground",
              pathname === "/chat/agents" && "bg-muted text-foreground",
            )}
          >
            <LayoutGrid className="h-4 w-4" />
            Agents
          </Link>
        </div>
        <HistoryPane pathname={pathname} isOrgAdmin={me.role === "tenant_admin"} />
        <SidebarFooter email={me.email} role={me.role} builder={builder} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-14 items-center border-b border-border px-3 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            onClick={() => setNavOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Brand variant="mark" className="ml-2" />
        </div>
        <Outlet />
      </div>
    </div>
  );
}

/**
 * The history pane. Everyone sees their own chats. An org admin also gets an
 * "All" view of every chat in the organisation (read-only), which replaced the
 * separate Conversations page: private by default, with the admin's wider view
 * one deliberate click away rather than mixed into their own list.
 */
function HistoryPane({ pathname, isOrgAdmin }: { pathname: string; isOrgAdmin: boolean }) {
  const inOrg = pathname.startsWith("/chat/org");
  const [view, setView] = React.useState<"mine" | "all">(inOrg ? "all" : "mine");
  // Following a link into someone else's chat switches the pane to match.
  React.useEffect(() => {
    if (inOrg) setView("all");
  }, [inOrg]);

  const segments = pathname.split("/");
  if (!isOrgAdmin) return <ConversationList activeId={segments[2]} />;
  return (
    <>
      <div className="mx-3 mt-4 grid grid-cols-2 rounded-md bg-muted p-0.5 text-xs font-medium">
        {(["mine", "all"] as const).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            onClick={() => setView(v)}
            className={cn(
              "rounded px-2 py-1 transition-colors",
              view === v
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {v === "mine" ? "My chats" : "All conversations"}
          </button>
        ))}
      </div>
      {view === "mine" ? (
        <ConversationList activeId={inOrg ? undefined : segments[2]} />
      ) : (
        <OrgConversationList activeId={inOrg ? segments[3] : undefined} />
      )}
    </>
  );
}

/** Every chat in the organisation, newest first — org admins only. */
function OrgConversationList({ activeId }: { activeId?: string }) {
  const [email, setEmail] = React.useState("");
  const [applied, setApplied] = React.useState("");
  const { data, isLoading, isError } = useQuery({
    queryKey: qk.orgConversations(applied),
    queryFn: () => listOrgConversations(applied || undefined),
  });
  return (
    <nav className="mt-3 flex-1 overflow-y-auto px-3 pb-3" aria-label="All conversations">
      <form
        className="relative mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(email.trim());
        }}
      >
        <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-2 text-xs"
          placeholder="Filter by user email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Filter by user email"
        />
      </form>
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : isError ? (
        <p className="px-3 py-2 text-xs text-destructive">Could not load conversations.</p>
      ) : (data ?? []).length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">
          {applied ? `Nothing from ${applied}.` : "Nobody has chatted with an agent yet."}
        </p>
      ) : (
        (data ?? []).map((c) => (
          <Link
            key={c.id}
            href={`/chat/org/${c.id}`}
            className={cn(
              "block rounded-md px-3 py-1.5 hover:bg-muted",
              c.id === activeId && "bg-muted",
            )}
            title={`${c.title} · ${c.user_email ?? ""} · ${c.agent_name}`}
          >
            <div className="flex items-center gap-2 text-sm">
              <Eye className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{c.title}</span>
            </div>
            <div className="truncate pl-5 text-xs text-muted-foreground">
              {c.user_email} · {c.agent_name}
              {c.is_evaluation ? " · evaluation" : ""}
            </div>
          </Link>
        ))
      )}
    </nav>
  );
}

/** Today / Yesterday / Previous 7 days / Older — how chat apps group history. */
function groupOf(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 86_400_000;
  if (d.getTime() >= startOfToday) return "Today";
  if (d.getTime() >= startOfToday - day) return "Yesterday";
  if (d.getTime() >= startOfToday - 7 * day) return "Previous 7 days";
  return "Older";
}

function ConversationList({ activeId }: { activeId?: string }) {
  const router = useRouter();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: qk.chatConversations,
    queryFn: listConversations,
  });
  const [renaming, setRenaming] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState("");
  const [toDelete, setToDelete] = React.useState<ChatConversation | null>(null);

  const rename = useMutation({
    mutationFn: (v: { id: string; title: string }) => renameConversation(v.id, v.title),
    onSuccess: async () => {
      setRenaming(null);
      await qc.invalidateQueries({ queryKey: qk.chatConversations });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (c: ChatConversation) => deleteConversation(c.id),
    onSuccess: async (_r, c) => {
      setToDelete(null);
      await qc.invalidateQueries({ queryKey: qk.chatConversations });
      if (c.id === activeId) router.replace("/chat");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const groups = React.useMemo(() => {
    const out = new Map<string, ChatConversation[]>();
    for (const c of data ?? []) {
      const g = groupOf(c.updated_at);
      out.set(g, [...(out.get(g) ?? []), c]);
    }
    return [...out.entries()];
  }, [data]);

  return (
    <nav className="mt-4 flex-1 overflow-y-auto px-3 pb-3" aria-label="Conversations">
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">Your chats will appear here.</p>
      ) : (
        groups.map(([label, items]) => (
          <div key={label} className="mb-4">
            <div className="px-3 pb-1 text-xs font-medium text-muted-foreground">{label}</div>
            {items.map((c) =>
              renaming === c.id ? (
                <form
                  key={c.id}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (draft.trim()) rename.mutate({ id: c.id, title: draft.trim() });
                  }}
                >
                  <input
                    autoFocus
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => setRenaming(null)}
                    onKeyDown={(e) => e.key === "Escape" && setRenaming(null)}
                    aria-label="Conversation title"
                  />
                </form>
              ) : (
                <div
                  key={c.id}
                  className={cn(
                    "group flex items-center rounded-md hover:bg-muted",
                    c.id === activeId && "bg-muted",
                  )}
                >
                  <Link
                    href={`/chat/${c.id}`}
                    className="flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-sm"
                    title={`${c.title} · ${c.agent_name}`}
                  >
                    <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate">{c.title}</span>
                  </Link>
                  <div className="hidden shrink-0 pr-1 group-hover:flex">
                    <button
                      type="button"
                      className="rounded p-1 text-muted-foreground hover:text-foreground"
                      aria-label={`Rename ${c.title}`}
                      onClick={() => {
                        setDraft(c.title);
                        setRenaming(c.id);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded p-1 text-muted-foreground hover:text-destructive"
                      aria-label={`Delete ${c.title}`}
                      onClick={() => setToDelete(c)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ),
            )}
          </div>
        ))
      )}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Delete this chat?"
        description={`“${toDelete?.title ?? ""}” and its messages will be removed.`}
        confirmLabel="Delete"
        pending={remove.isPending}
        onConfirm={() => toDelete && remove.mutate(toDelete)}
      />
    </nav>
  );
}

function SidebarFooter({
  email,
  role,
  builder,
}: {
  email: string;
  role: string;
  builder: boolean;
}) {
  const router = useRouter();
  const qc = useQueryClient();
  const { theme, setTheme } = useTheme();
  const signOut = useMutation({
    mutationFn: logout,
    onSettled: () => {
      qc.clear();
      router.replace("/login");
    },
  });
  return (
    <div className="border-t border-border p-3">
      {builder ? (
        <Link
          href="/dashboard"
          className="mb-2 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Settings2 className="h-4 w-4" />
          Open console
        </Link>
      ) : null}
      <div className="flex items-center gap-2 px-1">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{email}</div>
          <div className="text-xs text-muted-foreground">
            {role === "tenant_admin"
              ? "Admin"
              : role === "engineer"
                ? "Engineer"
                : role === "evaluator"
                  ? "Evaluator"
                  : "Member"}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Toggle theme"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        >
          <Sun className="h-4 w-4 dark:hidden" />
          <Moon className="hidden h-4 w-4 dark:block" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Sign out"
          disabled={signOut.isPending}
          onClick={() => signOut.mutate()}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
